import React from 'react';
import { styled } from '@components-shared/src/stitches';
import { useStickyPinned } from '../../hooks/useStickyPinned';
import { StyledScroll } from '../containers/RadixScroll';

export const LoadingContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  marginTop: 4,
  marginBottom: 4,
});

export const NoResultsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  gap: '16px',
  padding: '32px 16px',
});

export const NoResultsTitle = styled('p', {
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '20px',
  color: '$neutral800',
});

export const NoResultsSubTitle = styled('p', {
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '18px',
  color: '$neutral800',
});

export const SearchDivider = styled('div', {
  backgroundColor: '$neutral100',
  height: 1,
  width: '95%',
});

export const DividerContainer = styled('div', {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  color: '$grey100',
  paddingTop: '8px',
  paddingBottom: '8px',
});

export const SearchResultsContainer = styled('div', {
  height: 'fit-content',
  '-webkit-transform': 'translateZ(0)', // This avoids SectionHeader overlaying SearchResultsContainer scollbar on Safari
  variants: {
    loadingState: {
      true: {
        height: '200px',
      },
    },
  },
});

export const SectionHeader = styled('h2', {
  position: 'sticky',
  top: '-1px',
  zIndex: '$above1',
  background: '$white',
  padding: '0.5rem 1.5rem 0.375rem 1.125rem',
  color: '$neutral500',
  fontFamily: '$base',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '14px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',

  transition: 'box-shadow 0.2s',

  '&.is-pinned': {
    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.12)',
  },

  variants: {
    isMobile: {
      true: {
        base: 'S',
        color: '$neutral500',
        textTransform: 'uppercase',
      },
    },
  },
});

export const ScrollableContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  position: 'absolute',
  marginTop: '1rem',
  left: 0,
  width: '100%',
  '-webkit-transform': 'translateZ(0)', // This avoids SectionHeader overlaying SearchResultsContainer scollbar on Safari

  '@tablet': {
    marginTop: 0,
    position: 'unset',
    top: 'unset',
    left: 'unset',
    width: 'unset',
    color: '$grey900',

    background: '$white',
  },

  variants: {
    loadingState: {
      true: {
        height: '200px',
      },
    },
  },
  ...StyledScroll,

  '&::-webkit-scrollbar': {
    background: '$neutral200',
    width: '3px',
    borderRadius: 10,
  },
  '&::-webkit-scrollbar-thumb': {
    background: '$neutral400',
    borderRadius: 10,
  },
});

export function SearchRow({
  children,
  header,
  isMobile,
}: {
  children: React.ReactNode;
  header: string;
  isMobile?: boolean;
}) {
  const stickyRef = useStickyPinned<HTMLHeadingElement>();

  return (
    <>
      <SectionHeader ref={stickyRef} isMobile={isMobile}>
        {header}
      </SectionHeader>
      {children}
    </>
  );
}
